// covered-by: frontends/carrie/src/routes/ask-puntt/utils/__tests__/visibleComments.test.ts
/** Minimum fields for reject/visibility filtering (ticket + AskPunttReview comments). */
export type ReviewCommentVisibilityFields = {
  isDeleted?: boolean;
  liked?: boolean | null;
  isAI?: boolean;
  /** Set when a reviewer already rejected this finding, here or elsewhere. */
  previouslyRejected?: { judgmentId: string } | null;
};

/** Soft-deleted or reviewer-rejected comments stay out of review UI lists. */
export function isVisibleReviewComment(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return !comment.isDeleted && comment.liked !== false;
}

export function isRejectedReviewComment(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return !comment.isDeleted && comment.liked === false;
}

export function visibleReviewComments<T extends ReviewCommentVisibilityFields>(
  comments: T[] | undefined,
): T[] {
  return (comments ?? []).filter(isVisibleReviewComment);
}

export function rejectedReviewComments<T extends ReviewCommentVisibilityFields>(
  comments: T[] | undefined,
): T[] {
  return (comments ?? []).filter(isRejectedReviewComment);
}

export function rejectedReviewCommentCount(
  comments: ReviewCommentVisibilityFields[] | undefined,
): number {
  return rejectedReviewComments(comments).length;
}

/**
 * Findings she has already ruled on, sorted below the ones she has not.
 *
 * Demoted, never dropped: a finding she rejected before still appears, so a
 * wrongly learned rejection is visible rather than silently hiding a real
 * defect. Stable within each group, so the order she is used to survives.
 *
 * why here and not only in warhol: warhol sorts the `ReviewOutcome`, and the
 * panel renders christo's comments, which are a different list. Sorting only
 * there put demoted findings back at the top of the one she reads.
 */
export function demotedLast<T extends ReviewCommentVisibilityFields>(
  comments: T[],
): T[] {
  return [...comments].sort((a, b) => {
    const left = a.previouslyRejected ? 1 : 0;
    const right = b.previouslyRejected ? 1 : 0;
    return left - right;
  });
}

/** Comments shown in the review panel carousel (optionally includes rejected). */
export function reviewCommentsForPanel<T extends ReviewCommentVisibilityFields>(
  comments: T[] | undefined,
  includeRejected: boolean,
): T[] {
  const list = comments ?? [];
  if (includeRejected) {
    return demotedLast(list.filter((c) => !c.isDeleted));
  }
  return demotedLast(visibleReviewComments(list));
}

export function visibleAiCommentCount(
  comments: (ReviewCommentVisibilityFields & { isAI?: boolean })[] | undefined,
): number {
  return visibleReviewComments(comments).filter((c) => c.isAI).length;
}

export function visibleHumanCommentCount(
  comments: (ReviewCommentVisibilityFields & { isAI?: boolean })[] | undefined,
): number {
  return visibleReviewComments(comments).filter((c) => !c.isAI).length;
}
